import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { Button, Spinner } from '../../components/ui';
import { CheckCircle, AlertCircle, Clock } from 'lucide-react';
import './Onboarding.css';

// Meta redirects the customer here (https://whatsapp.jipate.com/onboarding/callback)
// when they finish its flow. We ask the backend to confirm the connection, and keep
// asking every few seconds until Meta's notification has arrived.
const POLL_MS = 4000;
const MAX_ATTEMPTS = 20; // about 80 seconds

const STATE = {
  CHECKING: 'checking',
  CONNECTED: 'connected',
  AMBIGUOUS: 'ambiguous',
  TIMEOUT: 'timeout',
  ERROR: 'error',
};

// One wide status panel: icon on the left, message in the middle, actions on the right.
const Panel = ({ tone, icon, title, text, children }) => (
  <section className={`ob-callback ob-callback--${tone}`} aria-live="polite">
    <div className="ob-callback-icon" aria-hidden="true">{icon}</div>
    <div className="ob-callback-text">
      <h2>{title}</h2>
      <p>{text}</p>
    </div>
    {children && <div className="ob-actions ob-callback-actions">{children}</div>}
  </section>
);

const OnboardingCallback = () => {
  const { request } = useApi();
  const navigate = useNavigate();
  const { search } = useLocation();

  const [state, setState] = useState(STATE.CHECKING);
  const [message, setMessage] = useState('');

  const attemptsRef = useRef(0);
  const timerRef = useRef(null);
  const mountedRef = useRef(false);
  const startedRef = useRef(false);

  const claim = useCallback(async () => {
    attemptsRef.current += 1;

    const params = new URLSearchParams(search);
    const wabaId = params.get('waba_id') || params.get('wabaId') || null;

    try {
      const res = await request('post', '/api/onboarding/claim', {
        wabaId,
        callbackQuery: search ? search.slice(0, 500) : null,
      });
      if (!mountedRef.current) return;

      if (res.result === 'CONNECTED') {
        setState(STATE.CONNECTED);
        setMessage(res.message || 'WhatsApp connected!');
        timerRef.current = setTimeout(() => navigate('/onboarding', { replace: true }), 2000);
        return;
      }

      if (res.result === 'AMBIGUOUS') {
        setState(STATE.AMBIGUOUS);
        setMessage(res.message || 'Please try again in a minute.');
        return;
      }

      // WAITING — Meta has not told us yet
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState(STATE.TIMEOUT);
        return;
      }
      timerRef.current = setTimeout(claim, POLL_MS);
    } catch (err) {
      if (!mountedRef.current) return;
      setState(STATE.ERROR);
      setMessage(err.response?.data?.error || 'Something went wrong while connecting WhatsApp.');
    }
  }, [request, navigate, search]);

  useEffect(() => {
    mountedRef.current = true;
    if (!startedRef.current) {
      startedRef.current = true;
      claim();
    }
    return () => {
      mountedRef.current = false;
      clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retry = () => {
    attemptsRef.current = 0;
    setState(STATE.CHECKING);
    setMessage('');
    claim();
  };

  return (
    <div className="ob-page ob-page--center">
      {state === STATE.CHECKING && (
        <Panel
          tone="checking"
          icon={<Spinner size="lg" />}
          title="Finishing your WhatsApp connection…"
          text="This usually takes a few seconds. Please keep this page open."
        />
      )}

      {state === STATE.CONNECTED && (
        <Panel
          tone="success"
          icon={<CheckCircle size={44} />}
          title="WhatsApp connected!"
          text={message}
        />
      )}

      {state === STATE.AMBIGUOUS && (
        <Panel tone="warning" icon={<Clock size={44} />} title="One moment" text={message}>
          <Button onClick={retry}>Try again</Button>
        </Panel>
      )}

      {state === STATE.TIMEOUT && (
        <Panel
          tone="warning"
          icon={<Clock size={44} />}
          title="Still waiting for Meta"
          text="We have not received confirmation from Meta yet. If you completed every step, wait a minute and check again."
        >
          <Button onClick={retry}>Check again</Button>
          <Button variant="outline" onClick={() => navigate('/onboarding')}>Back</Button>
        </Panel>
      )}

      {state === STATE.ERROR && (
        <Panel tone="error" icon={<AlertCircle size={44} />} title="We could not finish the connection" text={message}>
          <Button onClick={retry}>Try again</Button>
          <Button variant="outline" onClick={() => navigate('/onboarding')}>Back</Button>
        </Panel>
      )}
    </div>
  );
};

export default OnboardingCallback;